import {AppConfiguration, ChannelConfiguration, PineridgeChannel} from './pineridge-channel';
import {
  Event,
  onEvent,
  emitEvent,
  UserFrameExtractionEvent,
  UserStopFrameExtractionEvent,
  UserPublishLocalEvent,
  UserChangeVideoEvent,
  UserScreenshareLocalEvent,
  UserUpdateAdditionalTagsEvent,
  LocalStreamMuteStateChange,
  LocalStreamConnectionStateChange,
  LocalStreamVadStateChange,
  PineridgeChannelRemoteStreamPair,
  RemoteStreamMuteStateChange,
  RemoteStreamVadStateChange,
  RemoteStreamConnectionStateChange,
  PineridgeChannelLocalStreamPair,
  UserSetAudioDeviceEvent,
  UserEditChannelConfigEvent,
} from './events';
import * as U from './util';
import * as aircoreMedia from '@aircore/aircore-media';

const DEFAULT_USERID = 'pineridge-user';
const DEFAULT_CHANNELID = 'pineridge-channel';
const DEFAULT_ALLOW_PUBLISH_AUDIO = true;
const DEFAULT_ALLOW_PUBLISH_VIDEO = true;
const DEFAULT_ALLOW_PUBLISH_SCREENSHARE = true;
const DEFAULT_ALLOW_BOT_CHAT = true;
const DEFAULT_ALLOW_VOICE_DATA_COLLECTION = true;
const DEFAULT_PROVISIONING_URL = new URL('https://yosemite.stage.cantina.com');

export type CameraParams = {
  videoDevice: MediaDeviceInfo;
  videoConfig?: string;
  manualConstraints?: MediaTrackConstraints;
};

export const VIDEO_CONFIG_OPTIONS = {
  '640x480x30': {width: 640, height: 480, frameRate: 30},
  '640x480x24': {width: 640, height: 480, frameRate: 24},
  '640x480x15': {width: 640, height: 480, frameRate: 15},
  '480x640x30': {width: 480, height: 640, frameRate: 30},
  '960x540x30': {width: 960, height: 540, frameRate: 30},
  '540x960x30': {width: 540, height: 960, frameRate: 30},
  '1280x720x30': {width: 1280, height: 720, frameRate: 30},
  '1920x1080x30': {width: 1920, height: 1080, frameRate: 30},
  '1080x1920x30': {width: 1080, height: 1920, frameRate: 30},
  '2000x2000x30': {width: 2000, height: 2000, frameRate: 30},
};

export function isValidPAK(pak: string): boolean {
  return pak.length > 0 && pak.startsWith('pub');
}

enum URLKey {
  PAK = 'PAK',
  SAT = 'SAT',
  PROVISIONING_URL = 'PRV',
  QA_MODE = 'QAMODE',
  USER_ID = 'UID',
  CHANNEL_ID = 'CID',
  ALLOW_PUBLISH_AUDIO = 'ALLOWPUBLISHAUDIO',
  ALLOW_PUBLISH_VIDEO = 'ALLOWPUBLISHVIDEO',
  ALLOW_PUBLISH_SCREENSHARE = 'ALLOWPUBLISHSCREENSHARE',
  ALLOW_BOT_CHAT = 'ALLOWBOTCHAT',
  ALLLOW_VOICE_DATA_COLLECTION = 'ALLOWVOICEDATACOLLECTION',
  TOKEN_LIFETIME_SEC = 'TOKENLIFETIME',
  USER_AGENT = 'USERAGENT',
}

export class PineridgeController {
  static _instance: PineridgeController;
  engine: aircoreMedia.Engine;
  channels: PineridgeChannel[] = [];
  defaultAppConfig: AppConfiguration;
  isUserAgentSet: boolean = false;
  configuredURL = DEFAULT_PROVISIONING_URL;
  approvePlayCallbacks: (() => void)[] = [];
  noUI = false;
  videoDevice: Partial<MediaDeviceInfo> = {deviceId: undefined, label: 'default'};
  videoConfig: string | null = null;
  additionalTags: Array<string> = [];
  manualConstraints: MediaTrackConstraints | null = null;
  channelPendingSatUpdate: PineridgeChannel | null = null;

  private constructor() {
    U.log('PineridgeController constructor++');
    U.appLog(`Aircore Media SDK version: ${aircoreMedia.Engine.frameworkVersion}`);
    this.engine = aircoreMedia.Engine.sharedInstance;
    this.defaultAppConfig = this._initializeDefaultAppConfig();
    // @ts-expect-error - we are patching bigsur into the window object for QA automation purposes
    window.bigsur = aircoreMedia;
    // Setup some stuff for the aircore engine
    U.appLog(`Processing URL parameters for URL: ${window.location.href}`);
    const params = new URLSearchParams(window.location.search);
    // QA needs a bigsur public API test environment to run TestCafe scripts against
    // Lets provide a URL parameter that disables the pineridge UI so that the QA scripts can run
    if (params.has(URLKey.QA_MODE)) {
      this.noUI = true;
    } else {
      if (params.has(URLKey.PROVISIONING_URL)) {
        const PRV = params.get(URLKey.PROVISIONING_URL) || '';
        if (PRV.length) {
          U.appLog(`PRV found in URL: ${PRV}`);
          this.configuredURL = new URL(`https://${PRV}`);
        }
      }

      aircoreMedia.Engine.sharedInstance.setMediaServiceUrl(this.configuredURL);
      if (params.has(URLKey.SAT)) {
        const SAT = params.get(URLKey.SAT) || '';
        if (SAT.length > 0) {
          if (SAT.startsWith('sess')) {
            U.appLog(`SAT found in URL: ${SAT}`);
            this._addChannelFromSAT(SAT);
          } else {
            U.appLog(`Invalid SAT found in URL: ${SAT}`);
            U.appLog('SAT must start with "sess"');
          }
        }
      } else if (this.defaultAppConfig.pak.length > 0) {
        if (this.defaultAppConfig.pak.startsWith('pub')) {
          U.appLog(`PAK found: ${this.defaultAppConfig.pak}`);
          this._addChannelFromPAK(this.defaultAppConfig);
        } else {
          U.appLog(`Invalid PAK found: ${this.defaultAppConfig.pak}`);
          U.appLog('PAK must start with "pub"');
        }
      }

      onEvent(Event.USER_BEGIN_ADD_PAK_CHANNEL, () => {
        emitEvent(Event.PINERIDGE_ADD_CHANNEL_FROM_PAK, this.defaultAppConfig);
      });

      onEvent(Event.USER_FINISH_ADD_PAK_CHANNEL, (config: ChannelConfiguration) => {
        this._addChannelFromPAK(config);
      });

      onEvent(Event.USER_FINISH_EDIT_CHANNEL, ({pineridgeChannel, channelConfig}: UserEditChannelConfigEvent) => {
        pineridgeChannel.config = channelConfig;
      });

      onEvent(Event.USER_EDIT_CHANNEL, (channel: PineridgeChannel) => {
        emitEvent(Event.PINERIDGE_EDIT_CHANNEL, channel);
      });

      // This controller manages what happens when the user does some action that should result in something
      // other than a UI update. For example, when the user clicks the Join button, the controller needs to
      // tell the aircore engine to join the channel. The controller also needs to listen for events from
      // the aircore engine and then tell the UI to update itself.
      onEvent(Event.USER_JOIN_CHANNEL, (pineridgeChannel: PineridgeChannel) => {
        pineridgeChannel.mediaChannel.join();
      });

      onEvent(Event.USER_LEAVE_CHANNEL, async (pineridgeChannel: PineridgeChannel) => {
        pineridgeChannel.mediaChannel.leave();
      });

      onEvent(Event.USER_REMOVE_CHANNEL, async (pineridgeChannel: PineridgeChannel) => {
        if (pineridgeChannel.mediaChannel.joinState !== aircoreMedia.ChannelJoinState.Terminated) {
          pineridgeChannel.mediaChannel.leave();
        }

        this.channels = this.channels.filter(c => c.pineridgeChannelId !== pineridgeChannel.pineridgeChannelId);
        emitEvent(Event.PINERIDGE_CHANNEL_REMOVED, pineridgeChannel);
      });

      onEvent(Event.USER_MUTE_REMOTE_AUDIO, async (stream: aircoreMedia.RemoteStream) => {
        try {
          await stream.muteAudio(true);
        } catch (e) {
          U.appLog(`Exception thrown when trying to mute audio on remote stream: ${e}`);
        }
      });
      onEvent(Event.USER_UNMUTE_REMOTE_AUDIO, async (stream: aircoreMedia.RemoteStream) => {
        try {
          await stream.muteAudio(false);
        } catch (e) {
          U.appLog(`Exception thrown when trying to unmute audio on remote stream: ${e}`);
        }
      });
      onEvent(Event.USER_MUTE_REMOTE_VIDEO, async (stream: aircoreMedia.RemoteStream) => {
        try {
          await stream.muteVideo(true);
        } catch (e) {
          U.appLog(`Exception thrown when trying to mute video on remote stream: ${e}`);
        }
      });
      onEvent(Event.USER_UNMUTE_REMOTE_VIDEO, async (stream: aircoreMedia.RemoteStream) => {
        try {
          await stream.muteVideo(false);
        } catch (e) {
          U.appLog(`Exception thrown when trying to unmute video on remote stream: ${e}`);
        }
      });
      onEvent(Event.USER_MUTE_LOCAL_AUDIO, async (stream: aircoreMedia.LocalStream) => {
        try {
          await stream.muteAudio(true);
        } catch (e) {
          U.appLog(`Exception thrown when trying to mute local stream audio: ${e}`);
        }
      });
      onEvent(Event.USER_UNMUTE_LOCAL_AUDIO, async (stream: aircoreMedia.LocalStream) => {
        try {
          await stream.muteAudio(false);
        } catch (e) {
          U.appLog(`Exception thrown when trying to unmute local stream audio: ${e}`);
        }
      });
      onEvent(Event.USER_MUTE_LOCAL_VIDEO, async (stream: aircoreMedia.LocalStream) => {
        try {
          await stream.muteVideo(true);
        } catch (e) {
          U.appLog(`Exception thrown when trying to mute local stream video: ${e}`);
          stream.videoCamera?.stop();
        }
      });
      onEvent(Event.USER_UNMUTE_LOCAL_VIDEO, async (stream: aircoreMedia.LocalStream) => {
        try {
          await stream.muteVideo(false);
        } catch (e) {
          U.appLog(`Exception thrown when trying to unmute local stream video: ${e}`);
          stream.videoCamera?.stop();
        }
      });
      onEvent(
        Event.USER_START_FRAME_EXTRACTION_LOCAL_VIDEO,
        ({stream, frameRate, frameElementId, setFrameExtraction}: UserFrameExtractionEvent) => {
          try {
            stream.startExtractingFrames(frameRate, 150, 1, (uri: string) => {
              const e = window.document.getElementById(frameElementId) as HTMLMediaElement;
              if (e) e.src = uri;
            });
            setFrameExtraction(true);
          } catch (e) {
            U.appLog(`Exception thrown when trying to start local frame extraction: ${e}`);
          }
        }
      );
      onEvent(
        Event.USER_STOP_FRAME_EXTRACTION_LOCAL_VIDEO,
        ({stream, frameElementId, setFrameExtraction}: UserStopFrameExtractionEvent) => {
          try {
            stream.stopExtractingFrames();
            const e = window.document.getElementById(frameElementId) as HTMLMediaElement;
            if (e) e.src = '';
            setFrameExtraction(false);
          } catch (e) {
            U.appLog(`Exception thrown when trying to stop local frame extraction: ${e}`);
          }
        }
      );
      onEvent(
        Event.USER_PUBLISH_LOCAL,
        async ({
          channel,
          initialAudioMute,
          initialVideoMute,
          publishAudioOnly,
          audioDevice,
          cameraParams,
          additionalTags,
        }: UserPublishLocalEvent) => {
          U.appLog(
            `Publishing local stream with enableAudio: initialAudioMute: ${initialAudioMute} initialVideoMute: ${initialVideoMute}`
          );
          if (channel.mediaChannel.joinState === 'joined') {
            const params = new aircoreMedia.LocalStreamParams();
            try {
              params.initialAudioMute = initialAudioMute;
              params.initialVideoMute = initialVideoMute;
              params.additionalTags = additionalTags;
              let metadata;
              try {
                metadata = channel.config.metadata
                  ? new aircoreMedia.StreamMetadata(channel.config.metadata)
                  : undefined;
              } catch (e) {
                U.appLog(`Exception thrown when trying to set metadata: ${e}`);
              }
              params.metadata = metadata;
              params.audioDeviceId = audioDevice?.deviceId;

              if (!publishAudioOnly) {
                try {
                  params.source = await this._createCameraFromConstraints(cameraParams, !initialVideoMute);
                } catch (e: unknown) {
                  U.appLog(`Exception thrown when trying to get camera during local publish: ${e}`);
                  if (e instanceof Error && e.name === 'NotAllowedError') {
                    U.appLog('User must give the browser permission to use camera in order to publish a local stream.');
                  }

                  throw e;
                }
              }

              const localStream = channel.mediaChannel.createLocalStream(params);
              // Go ahead and start streaming right away - maybe add autoPublish option to wait for another button press
              await localStream.start();
              const streamId = localStream.streamId;
              U.appLog(`Publish local stream id: ${streamId}`);
            } catch (e) {
              U.appLog(`Exception thrown when trying to publish local stream: ${e}`);

              // If we have already captured the camera we want to stop here since we cannot publish
              if (params.source) {
                params.source.stop();
              }
            }
          } else {
            U.appLog('Ignoring PUBLISH_LOCAL event because channel is not joined');
          }
        }
      );
      onEvent(Event.USER_STOP_PUBLISH, async (stream: aircoreMedia.LocalStream) => {
        try {
          await stream.stop();
        } catch (e) {
          U.appLog(`Exception thrown when trying to stop local stream: ${e}`);
        }
      });
      onEvent(
        Event.USER_SCREENSHARE_LOCAL,
        async ({channel, publishVideoOnly, screenShareSourceId, additionalTags}: UserScreenshareLocalEvent) => {
          if (channel.mediaChannel.joinState === 'joined') {
            const params = new aircoreMedia.LocalStreamParams();
            params.additionalTags = additionalTags;
            try {
              let metadata;
              try {
                metadata = this.defaultAppConfig.metadata
                  ? new aircoreMedia.StreamMetadata(this.defaultAppConfig.metadata)
                  : undefined;
              } catch (e) {
                U.appLog(`Exception thrown when trying to set metadata: ${e}`);
              }
              params.metadata = metadata;

              try {
                const screenShare = await aircoreMedia.Engine.sharedInstance.deviceManager.getScreenshare(
                  !publishVideoOnly,
                  screenShareSourceId
                );
                params.source = screenShare;
                const localStream = channel.mediaChannel.createLocalStream(params);
                await localStream.start();
              } catch (e: unknown) {
                U.appLog(`Exception thrown when trying to get screenshare: ${e}`);
                if (e instanceof Error && e.name === 'NotAllowedError') {
                  U.appLog('User must give the browser permission to share in order to initiate screensharing');
                }

                throw e;
              }
            } catch (e) {
              U.appLog(`Exception thrown when trying to screenshare: ${e}`);
            }
          } else {
            U.appLog('Ignoring SCREENSHARE_LOCAL event because channel is not joined');
          }
        }
      );
      onEvent(Event.USER_APPROVE_PLAYBACK, () => {
        if (this.approvePlayCallbacks.length) {
          this.approvePlayCallbacks.forEach(callback => callback());
        }

        // Clear the list once all callbacks have been executed
        this.approvePlayCallbacks.length = 0;
      });
      onEvent(Event.USER_MOBILE_PASTE, async () => {
        try {
          const clipboardData = await navigator.clipboard.readText();
          U.appLog(`Found in clipboard: ${clipboardData}`);
          this.handleClipboardData(clipboardData);
        } catch (error) {
          U.appLog(`Error reading clipboard: ${error}`);
        }
      });

      onEvent(Event.USER_UPDATE_APP_CONFIG, (config: AppConfiguration) => {
        this._setDefaultAppConfig(config);
      });

      onEvent(Event.USER_SET_USER_AGENT, (userAgent: string) => {
        if (this.isUserAgentSet) {
          U.appLog(`User Agent already set to ${this.defaultAppConfig.userAgent}`);
        } else {
          // Update *just* the user agent in the app config
          const updatedAppConfig = this.defaultAppConfig;
          updatedAppConfig.userAgent = userAgent;
          this._setDefaultAppConfig(updatedAppConfig);
          U.appLog(`User Agent set to ${this.defaultAppConfig.userAgent}`);

          // Set the new User Agent and re-add the channel so that it is reflected in the new channel
          this.engine.setUserAgent(userAgent);
        }
      });

      onEvent(Event.USER_SET_AUDIO_DEVICE, async ({stream, device}: UserSetAudioDeviceEvent) => {
        U.appLog(`Stream ${stream.streamId} changing audio device to ${device.label}`);
        try {
          await stream.changeAudioDevice(device.deviceId);
        } catch (error) {
          U.appLog(`Error changing audio device: ${error}`);
        }
      });

      onEvent(Event.USER_CHANGE_VIDEO, async ({stream, cameraParams, isAdd}: UserChangeVideoEvent) => {
        U.appLogO(`Stream ${stream.streamId} changing video settings`, cameraParams);
        let camera;
        try {
          camera = await this._createCameraFromConstraints(cameraParams, !stream.videoMuted);
          await stream.changeVideoDevice(camera);
          if (isAdd) {
            stream.muteVideo(false);
          }
        } catch (error) {
          U.appLog(`Exception thrown when trying to set video source: ${error}`);
          camera?.stop();
        }
      });

      onEvent(Event.USER_UPDATE_ADDITIONAL_TAGS, async ({stream, additionalTags}: UserUpdateAdditionalTagsEvent) => {
        try {
          await stream.updateAdditionalTags(additionalTags);
        } catch (error) {
          U.appLog(`Error changing tags: ${error}`);
        }
      });

      onEvent(Event.USER_LOG_STREAM_STATS, async (stream: aircoreMedia.LocalStream | aircoreMedia.RemoteStream) => {
        const streamStats = await stream.collectMediaStreamData();
        const videoStats = stream.collectVideoElementData();
        const baseStats = {
          stream: streamStats,
          videoElements: videoStats,
        };

        const message = stream instanceof aircoreMedia.LocalStream ? 'LocalStream stats' : 'RemoteStream stats';
        U.appLogO(message, baseStats);
      });

      onEvent(Event.USER_CHANNEL_PENDING_SAT_UPDATE, (channelPendingSatUpdate: PineridgeChannel | null) => {
        this.channelPendingSatUpdate = channelPendingSatUpdate;
      });

      // Setup global paste handler so we can receive a SAT from the clipboard
      window.addEventListener('paste', (e: ClipboardEvent) => {
        const clipboardData = e.clipboardData?.getData('Text') || '';
        this.handleClipboardData(clipboardData);
      });

      // Setup global callback for when safari blocks scripted playback
      aircoreMedia.Channel.allowAutoplayCallback = (play: () => void) => {
        this.approvePlayCallbacks.push(play);
        emitEvent(Event.PINERIDGE_AUTOSTART_BLOCKED);
      };
    }

    // listen for when browsers forces user to make a device selection for permissions
    aircoreMedia.Engine.sharedInstance.deviceManager.on(
      aircoreMedia.DeviceManagerNotification.UserSelectedDeviceInBrowserUi,
      (device: MediaDeviceInfo) => {
        if (device.kind === 'videoinput') {
          emitEvent(Event.USER_SET_VIDEO_DEVICE, device);
        }
      }
    );

    aircoreMedia.Engine.sharedInstance.deviceManager.on(aircoreMedia.DeviceManagerNotification.DeviceListUpdate, () => {
      emitEvent(Event.BIGSUR_DEVICE_LIST_UPDATED);
    });

    U.log('PineridgeController constructor--');

    window.addEventListener('beforeunload', this._disposeChannels.bind(this));
  }

  public static getInstance(): PineridgeController {
    return this._instance || (this._instance = new this());
  }

  private async handleClipboardData(clipboardData: string): Promise<void> {
    if (clipboardData.startsWith('sess')) {
      U.appLog(`Found SAT in clipboard: ${clipboardData}`);
      if (this.channelPendingSatUpdate) {
        U.appLog(`Updating channel ${this.channelPendingSatUpdate.loggingId} with SAT: ${clipboardData}`);
        emitEvent(Event.PINERIDGE_CHANNEL_SAT_UPDATED, this.channelPendingSatUpdate);
        try {
          await this.channelPendingSatUpdate.mediaChannel.updateSessionAuthToken(clipboardData);
        } catch (error) {
          U.appLog(`Failed to update SAT: ${error}`);
        }
      } else {
        U.appLog(`Creating channel from SAT: ${clipboardData}`);
        this._addChannelFromSAT(clipboardData);
      }
    } else if (isValidPAK(clipboardData)) {
      // User pasted a PAK, lets go ahead and use it. Note that using a PAK
      // also requires that a userId and channelId be provided, so we will
      // use the configured values, which are either the default or values
      // already retrieved from the URL or localStorage
      U.appLog(`Found PAK in clipboard: ${clipboardData}`);

      // If no pak is present or no channels are present, set the default pak
      if (this.defaultAppConfig.pak.length === 0 || this.channels.length === 0) {
        const updatedConfig = this.defaultAppConfig;
        updatedConfig.pak = clipboardData;
        this._setDefaultAppConfig(updatedConfig);
        emitEvent(Event.PINERIDGE_DEFAULT_PAK_UPDATED, clipboardData);
      }

      emitEvent(Event.PINERIDGE_PAK_PASTED, {...this.defaultAppConfig, pak: clipboardData});
    }
  }

  private async _createCameraFromConstraints(
    cameraParams: CameraParams,
    initializeCam: boolean
  ): Promise<aircoreMedia.Camera> {
    const {videoDevice, videoConfig, manualConstraints} = cameraParams;
    let camera = null;
    try {
      const videoDeviceId = videoDevice.deviceId === 'default' ? undefined : videoDevice.deviceId;
      if (manualConstraints) {
        const manualConstraintsWithDeviceId: MediaTrackConstraints = {
          ...manualConstraints,
          deviceId: videoDeviceId ? {exact: videoDeviceId} : undefined,
        };

        camera = await aircoreMedia.Engine.sharedInstance.deviceManager.getCameraWithConstraints(
          initializeCam,
          manualConstraintsWithDeviceId
        );
      } else if (videoConfig) {
        const config = VIDEO_CONFIG_OPTIONS[videoConfig as keyof typeof VIDEO_CONFIG_OPTIONS];
        camera = await aircoreMedia.Engine.sharedInstance.deviceManager.getCamera(
          initializeCam,
          videoDeviceId,
          config.width,
          config.height,
          config.frameRate
        );
      } else {
        camera = await aircoreMedia.Engine.sharedInstance.deviceManager.getCamera(initializeCam, videoDeviceId);
      }
    } catch (e: unknown) {
      U.appLog(`Exception thrown when trying to get camera during local publish: ${e}`);
      if (e instanceof Error) {
        if (e.name === 'NotAllowedError') {
          U.appLog('User must give the browser permission to use camera in order to publish a local stream.');
        } else if (e instanceof OverconstrainedError) {
          U.appLogO(`OverconstrainedError: ${e.constraint}`, {
            videoDevice,
            manualConstraints,
            videoConfig: VIDEO_CONFIG_OPTIONS[videoConfig as keyof typeof VIDEO_CONFIG_OPTIONS],
          });
        }
      }
      throw new Error('Camera initialization fail');
    }

    if (initializeCam) {
      U.appLogO('Initialized camera', {
        targetConstraints: camera.constraints.video,
        actualSettings: camera.settings,
      });
    }

    return camera;
  }

  private _addChannelFromSAT(sat: string): void {
    let channel: aircoreMedia.Channel | undefined;
    try {
      channel = this.engine.createChannel(sat);
    } catch (e) {
      U.appLog(`Error creating channel from SAT: ${e}`);
    }
    if (channel) {
      const config = {sat, metadata: this.defaultAppConfig.metadata};
      const pineridgeChannel = new PineridgeChannel(channel, config);
      this._addChannel(pineridgeChannel);
      channel.on(aircoreMedia.ChannelNotification.SessionAuthTokenNearingExpiry, () => {
        emitEvent(Event.BIGSUR_CHANNEL_SAT_NEARING_EXPIRY, pineridgeChannel);
      });
    }

    // If this is the first channel added, make SAT init the default
    if (this.channels.length === 1) {
      U.setVarInURL(URLKey.SAT, sat);
    }
  }

  /**
   * Generates a PAK config using URL vars and the browser's localStorage.
   *
   * Priority goes URL -> localStorage -> DEFAULT
   *
   * @returns The PAK configuration
   */
  private _initializeDefaultAppConfig(): AppConfiguration {
    const params = new URLSearchParams(window.location.search);
    const storedAppConfig = U.getAppConfigFromLocalStorage();

    let pak = '';
    const urlPAK = params.get(URLKey.PAK) || '';
    if (urlPAK.length > 0) {
      if (urlPAK.startsWith('pub')) {
        U.appLog(`PAK found in URL: ${urlPAK}`);
        pak = urlPAK;
      } else {
        U.appLog(`Invalid PAK found in URL: ${urlPAK}`);
        U.appLog('PAK must start with "pub"');
      }
    } else {
      U.appLog('No PAK found in URL');
      if (storedAppConfig && isValidPAK(storedAppConfig.pak)) {
        U.appLog(`PAK found in localStorage: ${storedAppConfig.pak}`);
        pak = storedAppConfig.pak;
      } else {
        U.appLog('No PAK found in localStorage, please paste a PAK');
      }
    }

    let userId = DEFAULT_USERID;
    const urlUserId = params.get(URLKey.USER_ID) || '';
    if (urlUserId.length > 0) {
      U.appLog(`UserId found in URL: ${urlUserId}`);
      userId = urlUserId;
    } else {
      U.appLog('No UserId found in URL');
      if (storedAppConfig) {
        U.appLog(`UserId found in localStorage: ${storedAppConfig.userId}`);
        userId = storedAppConfig.userId;
      } else {
        U.appLog(`No localStorage found, using default UserId: ${DEFAULT_USERID}`);
      }
    }

    let channelId = DEFAULT_CHANNELID;
    const urlChannelId = params.get(URLKey.CHANNEL_ID) || '';
    if (urlChannelId.length > 0) {
      U.appLog(`ChannelId found in URL: ${urlChannelId}`);
      channelId = urlChannelId;
    } else {
      U.appLog('No ChannelId found in URL');
      if (storedAppConfig) {
        U.appLog(`ChannelId found in localStorage: ${storedAppConfig.channelId}`);
        channelId = storedAppConfig.channelId;
      } else {
        U.appLog(`No localStorage found, using default ChannelId: ${DEFAULT_CHANNELID}`);
      }
    }

    let allowPublishAudio = DEFAULT_ALLOW_PUBLISH_AUDIO;
    const urlAllowPublishAudioRaw = params.get(URLKey.ALLOW_PUBLISH_AUDIO) || '';
    if (urlAllowPublishAudioRaw.length > 0) {
      const urlAllowPublishAudioInt = parseInt(urlAllowPublishAudioRaw);
      allowPublishAudio = !isNaN(urlAllowPublishAudioInt) && urlAllowPublishAudioInt > 0;
      U.appLog(`Allow Publish Audio found in URL: ${allowPublishAudio}`);
    } else {
      U.appLog('No Allow Publish Audio found in URL');
      if (storedAppConfig) {
        allowPublishAudio = storedAppConfig.allowPublishAudio;
        U.appLog(`Allow Publish Audio found in localStorage: ${storedAppConfig.allowPublishAudio}`);
      } else {
        U.appLog(`No localStorage found, using default Allow Publish Audio: ${DEFAULT_ALLOW_PUBLISH_AUDIO}`);
      }
    }

    let allowPublishVideo = DEFAULT_ALLOW_PUBLISH_VIDEO;
    const urlAllowPublishVideoRaw = params.get(URLKey.ALLOW_PUBLISH_VIDEO) || '';
    if (urlAllowPublishVideoRaw.length > 0) {
      const urlAllowPublishVideoInt = parseInt(urlAllowPublishVideoRaw);
      allowPublishVideo = !isNaN(urlAllowPublishVideoInt) && urlAllowPublishVideoInt > 0;
      U.appLog(`Allow Publish Video found in URL: ${allowPublishVideo}`);
    } else {
      U.appLog('No Allow Publish Video found in URL');
      if (storedAppConfig) {
        allowPublishVideo = storedAppConfig.allowPublishVideo;
        U.appLog(`Allow Publish Video found in localStorage: ${storedAppConfig.allowPublishVideo}`);
      } else {
        U.appLog(`No localStorage found, using default Allow Publish Video: ${DEFAULT_ALLOW_PUBLISH_VIDEO}`);
      }
    }

    let allowPublishScreenshare = DEFAULT_ALLOW_PUBLISH_SCREENSHARE;
    const urlAllowPublishScreenshareRaw = params.get(URLKey.ALLOW_PUBLISH_SCREENSHARE) || '';
    if (urlAllowPublishScreenshareRaw.length > 0) {
      const urlAllowPublishScreenshareInt = parseInt(urlAllowPublishScreenshareRaw);
      allowPublishScreenshare = !isNaN(urlAllowPublishScreenshareInt) && urlAllowPublishScreenshareInt > 0;
      U.appLog(`Allow Publish Screenshare found in URL: ${allowPublishScreenshare}`);
    } else {
      U.appLog('No Allow Publish Screenshare found in URL');
      if (storedAppConfig) {
        allowPublishScreenshare = storedAppConfig.allowPublishScreenShare;
        U.appLog(`Allow Publish Screenshare found in localStorage: ${storedAppConfig.allowPublishScreenShare}`);
      } else {
        U.appLog(
          `No localStorage found, using default Allow Publish Screenshare: ${DEFAULT_ALLOW_PUBLISH_SCREENSHARE}`
        );
      }
    }

    let allowBotChat = DEFAULT_ALLOW_BOT_CHAT;
    const urlAllowBotChatRaw = params.get(URLKey.ALLOW_BOT_CHAT) || '';
    if (urlAllowBotChatRaw.length > 0) {
      const urlAllowBotChatInt = parseInt(urlAllowBotChatRaw);
      allowBotChat = !isNaN(urlAllowBotChatInt) && urlAllowBotChatInt > 0;
      U.appLog(`Allow Bot Chat found in URL: ${allowBotChat}`);
    } else {
      U.appLog('No Allow Bot Chat found in URL');
      if (storedAppConfig) {
        allowBotChat = storedAppConfig.allowBotChat;
        U.appLog(`Allow Bot Chat found in localStorage: ${storedAppConfig.allowBotChat}`);
      } else {
        U.appLog(`No localStorage found, using default Allow Bot Chat: ${DEFAULT_ALLOW_BOT_CHAT}`);
      }
    }

    let allowVoiceDataCollection = DEFAULT_ALLOW_VOICE_DATA_COLLECTION;
    const urlAllowVoiceDataCollectionRaw = params.get(URLKey.ALLLOW_VOICE_DATA_COLLECTION) || '';
    if (urlAllowVoiceDataCollectionRaw.length > 0) {
      const urlAllowVoiceDataCollectionInt = parseInt(urlAllowVoiceDataCollectionRaw);
      allowVoiceDataCollection = !isNaN(urlAllowVoiceDataCollectionInt) && urlAllowVoiceDataCollectionInt > 0;
      U.appLog(`Allow Voice Data Collection found in URL: ${allowVoiceDataCollection}`);
    } else {
      U.appLog('No Allow Voice Data Collection found in URL');
      if (storedAppConfig) {
        allowVoiceDataCollection = storedAppConfig.allowVoiceDataCollection;
        U.appLog(`Allow Voice Data Collection found in localStorage: ${storedAppConfig.allowVoiceDataCollection}`);
      } else {
        U.appLog(
          `No localStorage found, using default Allow Voice Data Collection: ${DEFAULT_ALLOW_VOICE_DATA_COLLECTION}`
        );
      }
    }

    let tokenLifetimeSec: number | undefined = undefined;
    const urlTokenLifetimeSec = parseInt(params.get(URLKey.TOKEN_LIFETIME_SEC) || '');
    if (!isNaN(urlTokenLifetimeSec)) {
      U.appLog(`Token Lifetime found in URL: ${urlTokenLifetimeSec}`);
      tokenLifetimeSec = urlTokenLifetimeSec;
    } else {
      U.appLog('No Token Lifetime found in URL');
      if (storedAppConfig && storedAppConfig.tokenLifetimeSec) {
        U.appLog(`Token Lifetime found in localStorage: ${storedAppConfig.tokenLifetimeSec}`);
        tokenLifetimeSec = storedAppConfig.tokenLifetimeSec;
      } else {
        U.appLog('No Token Lifetime found in localStorage, leaving unset');
      }
    }

    if (urlTokenLifetimeSec) {
      tokenLifetimeSec = urlTokenLifetimeSec;
    } else if (storedAppConfig && storedAppConfig.tokenLifetimeSec) {
      tokenLifetimeSec = storedAppConfig.tokenLifetimeSec;
    }

    let userAgent: string | undefined = undefined;
    const urlUserAgent = params.get(URLKey.USER_AGENT) || '';
    if (urlUserAgent.length > 0) {
      U.appLog(`User Agent found in URL: ${urlUserAgent}`);
      userAgent = urlUserAgent;
    } else {
      U.appLog('No User Agent found in URL');
      if (storedAppConfig && storedAppConfig.userAgent) {
        U.appLog(`User Agent found in localStorage: ${storedAppConfig.userAgent}`);
        userAgent = storedAppConfig.userAgent;
      } else {
        U.appLog('No User Agent found in localStorage, leaving unset');
      }
    }

    const appConfig: AppConfiguration = {
      pak,
      userAgent,
      userId,
      channelId,
      allowPublishAudio,
      allowPublishVideo: true,
      allowPublishScreenShare: true,
      allowBotChat: true,
      allowVoiceDataCollection: true,
      tokenLifetimeSec,
      metadata: storedAppConfig && storedAppConfig.metadata ? storedAppConfig.metadata : undefined,
    };

    this._setDefaultAppConfig(appConfig);
    return appConfig;
  }

  private _addChannelFromPAK(config: ChannelConfiguration): void {
    if (!U.isPakConfiguration(config)) {
      throw new Error('Invalid PAK configuration');
    }

    const channelParams = new aircoreMedia.ChannelParams();
    channelParams.allowPublishAudio = config.allowPublishAudio;
    channelParams.allowPublishVideo = config.allowPublishVideo;
    channelParams.allowPublishScreenShare = config.allowPublishScreenShare;
    channelParams.allowBotChat = config.allowBotChat;
    channelParams.allowVoiceDataCollection = config.allowVoiceDataCollection;

    const channel = this.engine.createChannel(config.pak, config.userId, config.channelId, channelParams);

    // @ts-expect-error - this is to support QA; the public API does not support configurable token lifetime yet
    if (config.tokenLifetimeSec) channel._tokenLifetimeSec = config.tokenLifetimeSec;

    const pineridgeChannel = new PineridgeChannel(channel, config);
    this._addChannel(pineridgeChannel);

    // If this is the first channel added, make PAK init the default
    if (this.channels.length === 1) {
      U.removeVarFromURL(URLKey.SAT);
    }
  }

  /**
   * Pushes the given app config to the appConfig instance variable, the URL parameters, and local storage.
   * @param appConfig app configuration to persist
   */
  private _setDefaultAppConfig(appConfig: AppConfiguration): void {
    this.defaultAppConfig = appConfig;

    U.setVarInURL(URLKey.PAK, this.defaultAppConfig.pak);
    U.setVarInURL(URLKey.USER_ID, this.defaultAppConfig.userId);
    U.setVarInURL(URLKey.CHANNEL_ID, this.defaultAppConfig.channelId);
    U.setVarInURL(URLKey.ALLOW_PUBLISH_AUDIO, this.defaultAppConfig.allowPublishAudio ? '1' : '0');
    U.setVarInURL(URLKey.ALLOW_PUBLISH_VIDEO, this.defaultAppConfig.allowPublishVideo ? '1' : '0');
    U.setVarInURL(URLKey.ALLOW_PUBLISH_SCREENSHARE, this.defaultAppConfig.allowPublishScreenShare ? '1' : '0');
    U.setVarInURL(URLKey.ALLOW_BOT_CHAT, this.defaultAppConfig.allowBotChat ? '1' : '0');
    U.setVarInURL(URLKey.ALLLOW_VOICE_DATA_COLLECTION, this.defaultAppConfig.allowVoiceDataCollection ? '1' : '0');
    if (this.defaultAppConfig.tokenLifetimeSec) {
      U.setVarInURL(URLKey.TOKEN_LIFETIME_SEC, String(this.defaultAppConfig.tokenLifetimeSec));
    } else {
      U.removeVarFromURL(URLKey.TOKEN_LIFETIME_SEC);
    }
    if (this.defaultAppConfig.userAgent) {
      U.setVarInURL(URLKey.USER_AGENT, this.defaultAppConfig.userAgent);
    } else {
      U.removeVarFromURL(URLKey.USER_AGENT);
    }

    U.setAppConfigInLocalStorage(this.defaultAppConfig);
  }

  private _setLocalStreamListeners(stream: aircoreMedia.LocalStream): void {
    stream.on(
      aircoreMedia.LocalStreamNotification.AudioMuteStateDidChange,
      (state: aircoreMedia.LocalStreamMuteStateNotification) => {
        emitEvent(Event.BIGSUR_LOCAL_STREAM_AUDIO_MUTE_CHANGED, {stream, state} as LocalStreamMuteStateChange);
      }
    );
    stream.on(
      aircoreMedia.LocalStreamNotification.VideoMuteStateDidChange,
      (state: aircoreMedia.LocalStreamMuteStateNotification) => {
        emitEvent(Event.BIGSUR_LOCAL_STREAM_VIDEO_MUTE_CHANGED, {stream, state} as LocalStreamMuteStateChange);
      }
    );
    stream.on(
      aircoreMedia.LocalStreamNotification.ConnectionStateDidChange,
      (state: aircoreMedia.LocalStreamConnectionStateNotification) => {
        emitEvent(Event.BIGSUR_LOCAL_STREAM_CONNECTION_CHANGED, {
          stream,
          state,
        } as LocalStreamConnectionStateChange);
        U.appLogO('Local stream connection state changed: ', state);
      }
    );
    stream.on(
      aircoreMedia.LocalStreamNotification.VoiceActivityStateDidChange,
      (state: aircoreMedia.LocalStreamVoiceActivityNotification) => {
        emitEvent(Event.BIGSUR_LOCAL_STREAM_VAD_CHANGED, {
          stream,
          state,
        } as LocalStreamVadStateChange);
      }
    );
  }

  private _addChannel(pineridgeChannel: PineridgeChannel): void {
    this.channels.push(pineridgeChannel);
    emitEvent(Event.PINERIDGE_CHANNEL_ADDED, pineridgeChannel);

    const mediaChannel = pineridgeChannel.mediaChannel;

    mediaChannel.on(aircoreMedia.ChannelNotification.RemoteStreamWasAdded, (stream: aircoreMedia.RemoteStream) => {
      const eventData: PineridgeChannelRemoteStreamPair = {pineridgeChannel, stream};
      emitEvent(Event.BIGSUR_REMOTE_STREAM_ADDED, eventData);
      stream.on(
        aircoreMedia.RemoteStreamNotification.LocalAudioMuteStateDidChange,
        (state: aircoreMedia.RemoteStreamMuteStateNotification) => {
          emitEvent(Event.BIGSUR_REMOTE_STREAM_LOCAL_AUDIO_MUTE_CHANGED, {
            stream,
            state,
          } as RemoteStreamMuteStateChange);
        }
      );
      stream.on(
        aircoreMedia.RemoteStreamNotification.LocalVideoMuteStateDidChange,
        (state: aircoreMedia.RemoteStreamMuteStateNotification) => {
          emitEvent(Event.BIGSUR_REMOTE_STREAM_LOCAL_VIDEO_MUTE_CHANGED, {
            stream,
            state,
          } as RemoteStreamMuteStateChange);
        }
      );
      stream.on(
        aircoreMedia.RemoteStreamNotification.RemoteAudioMuteStateDidChange,
        (state: aircoreMedia.RemoteStreamMuteStateNotification) => {
          emitEvent(Event.BIGSUR_REMOTE_STREAM_REMOTE_AUDIO_MUTE_CHANGED, {
            stream,
            state,
          } as RemoteStreamMuteStateChange);
        }
      );
      stream.on(
        aircoreMedia.RemoteStreamNotification.RemoteVideoMuteStateDidChange,
        (state: aircoreMedia.RemoteStreamMuteStateNotification) => {
          emitEvent(Event.BIGSUR_REMOTE_STREAM_REMOTE_VIDEO_MUTE_CHANGED, {
            stream,
            state,
          } as RemoteStreamMuteStateChange);
        }
      );
      stream.on(
        aircoreMedia.RemoteStreamNotification.VoiceActivityStateDidChange,
        (state: aircoreMedia.RemoteStreamVoiceActivityNotification) => {
          emitEvent(Event.BIGSUR_REMOTE_STREAM_VAD_CHANGED, {
            stream,
            state,
          } as RemoteStreamVadStateChange);
        }
      );
      stream.on(
        aircoreMedia.RemoteStreamNotification.ConnectionStateDidChange,
        (state: aircoreMedia.RemoteStreamConnectionStateNotification) => {
          emitEvent(Event.BIGSUR_REMOTE_STREAM_CONNECTION_CHANGED, {
            stream,
            state,
          } as RemoteStreamConnectionStateChange);
        }
      );
    });
    mediaChannel.on(aircoreMedia.ChannelNotification.RemoteStreamWasRemoved, (stream: aircoreMedia.RemoteStream) => {
      const eventData: PineridgeChannelRemoteStreamPair = {pineridgeChannel, stream};
      emitEvent(Event.BIGSUR_REMOTE_STREAM_REMOVED, eventData);
    });
    mediaChannel.on(aircoreMedia.ChannelNotification.LocalStreamWasRemoved, (stream: aircoreMedia.LocalStream) => {
      const eventData: PineridgeChannelLocalStreamPair = {pineridgeChannel, stream};
      emitEvent(Event.BIGSUR_LOCAL_STREAM_REMOVED, eventData);
    });
    mediaChannel.on(
      aircoreMedia.ChannelNotification.ScreenShareStreamWasRemoved,
      (stream: aircoreMedia.LocalStream) => {
        const eventData: PineridgeChannelLocalStreamPair = {pineridgeChannel, stream};
        emitEvent(Event.BIGSUR_SCREEN_SHARE_STREAM_REMOVED, eventData);
      }
    );
    mediaChannel.on(
      aircoreMedia.ChannelNotification.JoinStateDidChange,
      async (stateData: aircoreMedia.ChannelJoinStateNotification) => {
        if (stateData.newJoinState === aircoreMedia.ChannelJoinState.Joined) {
          emitEvent(Event.BIGSUR_CHANNEL_JOINED, pineridgeChannel);
        } else if (stateData.newJoinState === aircoreMedia.ChannelJoinState.Terminated) {
          emitEvent(Event.BIGSUR_CHANNEL_LEFT, pineridgeChannel);
        } else if (stateData.newJoinState === aircoreMedia.ChannelJoinState.Rejoining) {
          emitEvent(Event.BIGSUR_CHANNEL_REJOINING, pineridgeChannel);
        } else if (stateData.newJoinState === aircoreMedia.ChannelJoinState.Joining) {
          emitEvent(Event.BIGSUR_CHANNEL_JOINING, pineridgeChannel);
        }
      }
    );
    mediaChannel.on(aircoreMedia.ChannelNotification.LocalStreamWasAdded, () => {
      const stream = mediaChannel.localStream;
      if (stream) {
        const eventData: PineridgeChannelLocalStreamPair = {pineridgeChannel, stream};
        emitEvent(Event.BIGSUR_LOCAL_STREAM_ADDED, eventData);
        this._setLocalStreamListeners(stream);
      } else {
        U.appLog('DANGER DANGER: Local stream was added, but channel.localStream was undefined');
      }
    });
    mediaChannel.on(aircoreMedia.ChannelNotification.ScreenShareStreamWasAdded, () => {
      const stream = mediaChannel.screenShareStream;
      if (stream) {
        const eventData: PineridgeChannelLocalStreamPair = {pineridgeChannel, stream};
        emitEvent(Event.BIGSUR_SCREEN_SHARE_STREAM_ADDED, eventData);
        this._setLocalStreamListeners(stream);
      } else {
        U.appLog('DANGER DANGER: Screen share stream was added, but channel.screenShareStream was undefined');
      }
    });
  }

  private _disposeChannels(): void {
    this.channels.forEach(c => c.mediaChannel.leave());
  }
}

export const Controller = PineridgeController.getInstance();
